import type { IElementConfig } from '../element/types';
import type { ILoaderConfig, ILoaderTitleConfig } from './types';

import { merge } from '../../helper';

import * as exElement from '../element';

// ----------------------------------------------------------------------------

type TypeLoaderTitleData = {
    config: ILoaderTitleConfig;
    gap: string;
    placement: 'bottom' | 'top' | 'left' | 'right';
};

// ----------------------------------------------------------------------------

function getTitleConfig(config: ILoaderConfig['title']): TypeLoaderTitleData {
    const titleConfig: ILoaderTitleConfig = typeof config === 'string' ? { html: config } : { ...config };

    const placement =
        titleConfig.placement === 'top' || titleConfig.placement === 'left' || titleConfig.placement === 'right'
            ? titleConfig.placement
            : 'bottom';
    const gap = typeof titleConfig.gap === 'string' ? titleConfig.gap : '12px';

    delete titleConfig.placement;
    delete titleConfig.gap;

    const defTitleConfig: ILoaderTitleConfig = {
        tag: 'div',
    };

    merge(defTitleConfig, titleConfig);

    return {
        config: defTitleConfig,
        gap,
        placement,
    };
}

// ----------------------------------------------------------------------------

function getIconConfig(config: ILoaderConfig['icon']): IElementConfig {
    if (config === true) {
        return {};
    }

    return config != null && typeof config === 'object' ? config : {};
}

// ----------------------------------------------------------------------------

function create(config: ILoaderConfig = {}): HTMLElement {
    // Default Config
    const defConfig: ILoaderConfig = {
        icon: true,
    };

    // Merge
    merge(defConfig, config);

    // ------------------------------------------------------------------------

    //
    const elements: Record<string, any> = {};

    // ------------------------------------------------------------------------

    // Create element (loader)
    const elLoader = exElement.create({
        merge: defConfig,
        class: {
            new: 'loader',
        },
    });
    elements.element = elLoader;

    // Create element (loader content)
    const elLoaderContent = exElement.create({
        attr: {
            'data-placement': 'bottom',
        },
        style: {
            '--loader-title-gap': '12px',
        },
        class: {
            new: 'loader-content',
        },
    });
    elLoader.appendChild(elLoaderContent);
    elements.content = elLoaderContent;

    // ------------------------------------------------------------------------

    // If using icon
    if (defConfig.icon != null && defConfig.icon !== false) {
        // Create element (loader icon)
        const elLoaderIcon = exElement.create({
            merge: getIconConfig(defConfig.icon),
            class: {
                new: 'loader-spinner',
            },
        });
        elLoaderContent.appendChild(elLoaderIcon);
        elements.icon = elLoaderIcon;
    }

    // If using title
    if (defConfig.title != null) {
        const titleData = getTitleConfig(defConfig.title);

        elLoaderContent.setAttribute('data-placement', titleData.placement);
        elLoaderContent.style.setProperty('--loader-title-gap', titleData.gap);

        // Create element (loader title)
        const elLoaderTitle = exElement.create({
            merge: titleData.config,
            class: {
                new: 'loader-title',
            },
        });
        elLoaderContent.appendChild(elLoaderTitle);
        elements.title = elLoaderTitle;
    }

    // ------------------------------------------------------------------------

    // Return
    return elements.element;
}

// ----------------------------------------------------------------------------

export { create };
